import PusherJS from 'pusher-js';
import type { Destroyable } from '$lib/types/utils';
import type {
    ChannelAuthorizationData,
    UserAuthenticationData,
} from 'pusher-js/types/src/core/auth/options';
import httpReq from '@datawrapper/shared/httpReq.js';
import { nanoid } from 'nanoid';

PusherJS.logToConsole = false;

type ChannelAuthorizationHandler = (args: {
    socketId: string;
    channelName: string;
}) => Promise<ChannelAuthorizationData | undefined | null>;

type UserAuthenticationHandler = (args: {
    socketId: string;
}) => Promise<UserAuthenticationData | undefined | null>;

export type PusherProps = {
    host: string;
    port: number | undefined;
    path: string | undefined;
    appKey: string;
    userAuthEndpoint: string;
    useTLS: boolean;
};

type ChannelMatcher = ((channelName: string) => boolean) | RegExp;
type GetPusherClient = (opts?: {
    channelAuthEndpoint: string;
    channelMatcher: ChannelMatcher;
}) => PusherJS;

export type PusherContext = {
    getClient: GetPusherClient;
} & Destroyable;

export type PusherStates =
    | 'initialized'
    | 'connecting'
    | 'connected'
    | 'disconnected'
    | 'unavailable'
    | 'failed';

export type PusherStateChangeStates = {
    previous: PusherStates;
    current: PusherStates;
};

export function createPusherContext({
    host,
    port,
    path,
    appKey,
    userAuthEndpoint,
    useTLS,
}: PusherProps): PusherContext {
    const channelAuthHandlers = new Map<
        string,
        {
            handler: ChannelAuthorizationHandler;
            channelMatcher: ChannelMatcher;
        }
    >();

    /**
     * Add a channel authorization endpoint.
     * Returns a function that removes the endpoint when called.
     */
    const addChannelAuthorizationEndpoint = (
        endpoint: string,
        channelMatcher: ChannelMatcher
    ): (() => void) => {
        const key = nanoid();
        channelAuthHandlers.set(key, {
            handler: getPusherAuthHandler<ChannelAuthorizationHandler>(endpoint),
            channelMatcher,
        });
        return () => {
            channelAuthHandlers.delete(key);
        };
    };

    const userAuthHandler = getPusherAuthHandler<UserAuthenticationHandler>(userAuthEndpoint);

    const pusher = new PusherJS(appKey, {
        cluster: 'localhost',
        wsHost: host,
        wsPort: port,
        wsPath: path,
        forceTLS: useTLS,
        enabledTransports: ['ws', 'wss'],
        userAuthentication: {
            transport: 'ajax',
            endpoint: '', // not required because we have a custom handler
            customHandler: async ({ socketId }, callback) => {
                try {
                    const res = await userAuthHandler({ socketId });
                    if (res) {
                        callback(null, res);
                        return;
                    }
                } catch (error) {
                    callback(error as Error, null);
                }
            },
        },
        channelAuthorization: {
            transport: 'ajax',
            endpoint: '', // not required because we have a custom handler
            customHandler: async ({ socketId, channelName }, callback) => {
                if (channelAuthHandlers.size === 0) {
                    console.error('No channel authorization handler found');
                    callback(new Error('No channel authorization handler registered'), null);
                    return;
                }

                for (const { handler, channelMatcher } of channelAuthHandlers.values()) {
                    if (typeof channelMatcher === 'function' && !channelMatcher(channelName)) {
                        continue;
                    }
                    if (channelMatcher instanceof RegExp && !channelMatcher.test(channelName)) {
                        continue;
                    }
                    try {
                        const res = await handler({ socketId, channelName });
                        // If a handler returns true, we don't need to call the next handler.
                        if (res) {
                            callback(null, res);
                            return;
                        }
                    } catch (err) {
                        callback(err as Error, null);
                        return;
                    }
                }
                console.error('No channel authorization handler found');
                callback(new Error('No channel authorization handler found'), null);
            },
        },
    });

    pusher.connection.bind('state_change', function ({ current }: PusherStateChangeStates) {
        if (['unavailable', 'failed'].includes(current)) {
            console.error(`Could not connect to the websocket server (state: ${current})`);
        }
    });

    pusher.signin();

    /**
     * Get a Pusher client with an optional custom channel authorization handler that is applied
     * to all channels matching the given matcher.
     *
     * Remember to call disconnect() on the returned client to clean up the custom channel authorization.
     */
    const getClient: GetPusherClient = (channelAuthOptions?: {
        channelAuthEndpoint: string;
        channelMatcher: ChannelMatcher;
    }) => {
        const removeChannelAuthorizationEndpoint = channelAuthOptions
            ? addChannelAuthorizationEndpoint(
                  channelAuthOptions.channelAuthEndpoint,
                  channelAuthOptions.channelMatcher
              )
            : () => void 0;

        const proxy = new Proxy(pusher, {
            get(target, propKey, receiver) {
                if (propKey === 'disconnect') {
                    // we override the disconnect method to destroy the channel auth handler
                    // this way, calling disconnect() on the proxy only destroys the channel auth handler
                    // but not the pusher instance itself
                    return function () {
                        removeChannelAuthorizationEndpoint();
                    };
                }
                return Reflect.get(target, propKey, receiver);
            },
        });

        return proxy;
    };

    return {
        getClient,
        destroy: () => {
            pusher.disconnect();
        },
    };
}

/**
 * Get the socket ID of a Pusher instance.
 * Waits for the connection to be established if it's not already.
 */
export async function getPusherSocketId(pusher: PusherJS): Promise<string | undefined> {
    return await new Promise<string | undefined>(resolve => {
        const getId = () => pusher.connection.socket_id;

        const id = getId();
        if (id) resolve(id);

        const onConnected = () => {
            pusher.unbind('connected', onConnected);
            resolve(getId());
        };

        pusher.connection.bind('connected', onConnected);
    });
}

function getPusherAuthHandler<T extends ChannelAuthorizationHandler | UserAuthenticationHandler>(
    endpoint: string
): T {
    return ((options: Parameters<T>[0]) =>
        httpReq.post(endpoint, {
            payload: options,
            baseUrl: `//${location.host}`,
        })) as T;
}
